import { MeasureUnit, UserModel, UserStatus, IUserManager, IdClaimModel, IUserData, ChangeStatusModel } from '@truckdown/accounts';
import context, { IAuthenticator, LoginWithUsernameModel, ApiResultModel, HttpMethod, ILookupService, ServiceLookupModel, ServiceModel, ServiceStatus, ServiceType } from '@truckdown/systems';
import { defineComponent, ref, Ref, onMounted } from 'vue';
import { isCsrOrAdmin, isSystemAdmin, isFleet, isGeneralAdmin, formatRadius, showMessage, getUserRoles, isUserInRole } from "../../Utilities/Utilities";
import editAdminSettings from "./EditAdminSettings/EditAdminSettings.vue";
import editUserInfo from "./EditUserInfo/EditUserInfo.vue";
import editUserPreferences from "./EditUserPreferences/EditUserPreferences.vue";
import editEmailAddress from "./EditEmailAddress/EditEmailAddress.vue";
import editMobileNumber from "./EditMobileNumber/EditMobileNumber.vue";
import editSecuritySettings from "./EditSecuritySettings/EditSecuritySettings.vue";
import changePassword from "./ChangePassword/ChangePassword.vue";
import moveUserAdmin from "./MoveUserAdmin/MoveUserAdmin.vue";

interface IData {
    isLoading: Ref<boolean>,
    isStatusEditable: Ref<boolean>,
    isStatusEditing: Ref<boolean>,
    changeStatusModel: Ref<ChangeStatusModel>,
    showEditAdminSettings: Ref<boolean>,
    showEditUserInfo: Ref<boolean>,
    showEditUserPreferences: Ref<boolean>,
    showEditEmailAddress: Ref<boolean>,
    showEditMobileNumber: Ref<boolean>,
    showEditSecuritySettings: Ref<boolean>,
    showChangePassword: Ref<boolean>,
    showRemoveProfilePicture: Ref<boolean>,
    showCsrAdd: Ref<boolean>,
    showCsrRemove: Ref<boolean>,
    showSystemAdminAdd: Ref<boolean>,
    showMoveUserAdmin: Ref<boolean>,
    showDeleteUserModal: Ref<boolean>,
    showClaims: Ref<boolean>,
    showSettings: Ref<boolean>,
    userProfile: Ref<UserModel>,
    allServices: Ref<ServiceModel[]>,
    timeZones: Ref<string[]>,
    isCsrOrAdmin: Ref<boolean>
    isFleet: Ref<boolean>,
    isGeneralAdmin: Ref<boolean>,
    isSystemAdmin: Ref<boolean>,
    emailReturnUrl: Ref<string>,
    currentStatus: Ref<UserStatus>
}

const getInitialData = function (props: any) {

    let emailReturnUrl = encodeURIComponent(window.location.origin + "/accounts/admin/user/" + props.userProfile.id);

    var changeStatusModel = {
        id: props.userProfile.id,
        status: props.userProfile.status
    } as ChangeStatusModel;

    var statusEditableCheck = isGeneralAdmin() &&
        context.user?.id != props.userProfile.id &&
        context.user?.accountId == props.userProfile.aid &&
        (props.userProfile.status == UserStatus.Active || props.userProfile.status == UserStatus.Inactive);

    return {
        isLoading: ref<boolean>(false),
        isStatusEditing: ref<boolean>(false),
        isStatusEditable: ref<boolean>(statusEditableCheck),
        showEditAdminSettings: ref<boolean>(false),
        showEditUserInfo: ref<boolean>(false),
        showEditUserPreferences: ref<boolean>(false),
        showEditSecuritySettings: ref<boolean>(false),
        showEditEmailAddress: ref<boolean>(false),
        showEditMobileNumber: ref<boolean>(false),
        showChangePassword: ref<boolean>(false),
        showRemoveProfilePicture: ref<boolean>(false),
        showCsrAdd: ref<boolean>(false),
        showCsrRemove: ref<boolean>(false),
        showSystemAdminAdd: ref<boolean>(false),
        showMoveUserAdmin: ref<boolean>(false),
        showDeleteUserModal: ref<boolean>(false),
        showClaims: ref<boolean>(false),
        showSettings: ref<boolean>(false),
        userProfile: ref<UserModel>(props.userProfile),
        allServices: ref<ServiceModel[]>([]),
        timeZones: ref<string[]>(props.timeZones),
        isCsrOrAdmin: ref<boolean>(isCsrOrAdmin()),
        isFleet: ref<boolean>(isFleet()),
        isGeneralAdmin: ref<boolean>(isGeneralAdmin()),
        isSystemAdmin: ref<boolean>(isSystemAdmin()),
        emailReturnUrl: ref<string>(emailReturnUrl),
        changeStatusModel: ref<ChangeStatusModel>(changeStatusModel),
        currentStatus: ref<UserStatus>(props.userProfile.status)
    } as IData;
}

const closeModals = function (data: IData) {
    data.showEditAdminSettings.value = false;
    data.showEditUserInfo.value = false;
    data.showEditUserPreferences.value = false;
    data.showEditSecuritySettings.value = false;
    data.showEditEmailAddress.value = false;
    data.showEditMobileNumber.value = false;
    data.showChangePassword.value = false;
    data.showCsrAdd.value = false;
    data.showCsrRemove.value = false;
    data.showSystemAdminAdd.value = false;
    data.showRemoveProfilePicture.value = false;
    data.showMoveUserAdmin.value = false;
    data.showDeleteUserModal.value = false;
}

const allowExcludingTruckDown = function (userProfile: UserModel) {
    if (userProfile.status == UserStatus.Active &&
        userProfile.roles.indexOf("TruckDown CSR") < 0 &&
        userProfile.roles.indexOf("TruckDown Operations") < 0 &&
        userProfile.roles.indexOf("System Admin") < 0) {
        return true;
    } else {
        return false;
    }
}

const reloadUserModel = async function (data: IData) {
    if (!data.isLoading.value) {
        data.isLoading.value = true;

        try {
            let userData = await context.getService<IUserData>('IUserData');
            var response = await userData.getUser(data.userProfile.value.id);

            if (response && response != null) {
                data.userProfile.value = response;
            } else {
                window.location.href = window.location.pathname + window.location.search + window.location.hash;
            }
        } catch (err) {
            window.location.href = window.location.pathname + window.location.search + window.location.hash;
        } finally {
            data.isLoading.value = false;
        }
    }
}

const refreshUser = function () {
    let returnUrl = window.location.pathname + window.location.search + window.location.hash;
    window.location.href = '/systems/context/refresh?returnUrl=' + encodeURIComponent(returnUrl);
}

const changeStatusSave = async function (data: IData) {
    data.showDeleteUserModal.value = false;
    if (!data.isLoading.value) {
        data.isLoading.value = true;

        try {
            let userManager = await context.getService<IUserManager>('IUserManager');
            var response = await userManager.changeStatus(data.changeStatusModel.value);

            if (!response || !response.ok) {
                console.log("Error changing user status: " + response.message);
                showMessage("Error changing user status: " + response.message, "danger", true, true);
            } else {
                showMessage("User status successfully changed.", "success", true, true);
                if (response.data?.status == UserStatus.Purge) {
                    window.location.href = "/accounts/team-management";
                } else {
                    if (response.data) {
                        data.currentStatus.value = response.data.status;
                    } else {
                        window.location.href = window.location.pathname + window.location.search + window.location.hash;
                    }
                }
            }
        } catch (err) {
            console.log(err);
        } finally {
            data.isLoading.value = false;
            data.isStatusEditing.value = false;
        }
    }
}

const getMethods = function (data: IData, props: any, ctx: any) {
    return {
        getReceiveNewsletters: function (userProfile: UserModel) {
            var receiveNewsletters = userProfile.claims.find(c => {
                return c.type == "newsletter_group" && c.value == "marketing"
            });
            return receiveNewsletters != null;
        },
        getReceiveReports: function (userProfile: UserModel) {
            var receiveReports = userProfile.claims.find(c => {
                return c.type == "newsletter_group" && c.value == "reports"
            });
            return receiveReports != null;
        },
        getProfilePicture: function (userProfile: UserModel) {
            return context.profileImagePath + userProfile.settings.profilepic;
        },
        dateDisplay: function (dateUTC: Date | string): Date | string | undefined {
            if (dateUTC) {
                return new Date(dateUTC).toLocaleString();
            } else
                return undefined;
        },
        getMeasureUnit: function (userProfile: UserModel) {
            return MeasureUnit[userProfile.properties.measureUnit];
        },
        getUserStatus: function () {
            return UserStatus[data.currentStatus.value];
        },
        getDefaultSearchServices: function (userProfile: UserModel) {
            var searchDefaultServices = userProfile.claims.filter(c => {
                return c.type == "search_default_service";
            });

            var serviceCodes = [];
            for (var i = 0; i < searchDefaultServices.length; i++) {
                serviceCodes.push(searchDefaultServices[i].value);
            }

            var services = [];
            for (var i = 0; i < data.allServices.value.length; i++) {
                if (serviceCodes.indexOf(data.allServices.value[i].code) > -1) {
                    services.push(data.allServices.value[i].name);
                }
            }

            return services.length == 0 ? "None" : services.join(", ");
        },
        getSearchPrivateRadius: function (userProfile: UserModel) {
            var searchPrivateRadius = userProfile.claims.find(c => {
                return c.type == "search_private_radius";
            });

            return formatRadius(searchPrivateRadius?.value.toLowerCase() ?? "hundred");
        },
        getSearchPreferredRadius: function (userProfile: UserModel) {
            var searchPreferredRadius = userProfile.claims.find(c => {
                return c.type == "search_preferred_radius";
            });

            return formatRadius(searchPreferredRadius?.value.toLowerCase() ?? "hundred");
        },
        getSearchByDistance: function (userProfile: UserModel) {
            var searchByDistance = userProfile.claims.find(c => {
                return c.type == "search_distance_search";
            });

            return (searchByDistance?.value ?? "False") == "True";
        },
        getAllowRatings: function (userProfile: UserModel) {
            var allowRatings = userProfile.claims.find(c => {
                return c.type == "search_ratings";
            });

            return (allowRatings?.value ?? "False") == "True";
        },
        getEditNotes: function (userProfile: UserModel) {
            var editNotes = userProfile.claims.find(c => {
                return c.type == "search_edit_notes";
            });

            return (editNotes?.value ?? "False") == "True";
        },
        getPreferredVendors: function (userProfile: UserModel) {
            var preferredVendors = userProfile.claims.find(c => {
                return c.type == "search_preferred_vendors";
            });

            return (preferredVendors?.value ?? "False") == "True" && isFleet();
        },
        getAdvancedOptions: function (userProfile: UserModel) {
            var advancedOptions = userProfile.claims.find(c => {
                return c.type == "search_advanced_options";
            });

            return (advancedOptions?.value ?? "False") == "True" && isFleet();
        },
        getAllowedRateVendors: function (userProfile: UserModel) {
            var allowedRateVendors = userProfile.claims.find(c => {
                return c.type == "search_suppress_ratings";
            });

            return (allowedRateVendors?.value ?? "True") == "True";
        },
        getStatus: function (userProfile: UserModel) {
            return UserStatus[userProfile.status];
        },
        formatPhoneString: function (phone: string) {
            if (phone?.length == 10) {
                phone = phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
            }
            return phone;
        },
        showLoginas: function (userProfile: UserModel) {
            if (context.user?.id != userProfile.id) {
                return allowExcludingTruckDown(userProfile);
            } else {
                return false;
            }
        },
        showMoveUser: function (userProfile: UserModel) {
            if (context.user?.id != userProfile.id) {
                return allowExcludingTruckDown(userProfile);
            } else {
                return false;
            }
        },
        showChangeStatus: function (userProfile: UserModel) {
            if (isGeneralAdmin() && context.user?.id != userProfile.id &&
                (props.userProfile.status == UserStatus.Active || props.userProfile.status == UserStatus.Inactive)) {
                return true;
            }

            return false;
        },
        startEditingStatus: function () {
            data.isStatusEditing.value = true;
        },
        displayProfilePictureButtons: function (userProfile: UserModel) {
            return context.user?.id == userProfile.id;
        },
        canEditSecuritySettings: function (userProfile: UserModel) {
            if (isCsrOrAdmin() || (isGeneralAdmin() && context.user?.id != userProfile.id)) {
                return true;
            }
            return false;
        },
        cancelEditAdminSettings: function () {
            data.showEditAdminSettings.value = false;
        },
        cancelEditUserInfo: function () {
            data.showEditUserInfo.value = false;
        },
        cancelEditUserPreferences: function () {
            data.showEditUserPreferences.value = false;
        },
        cancelEditEmailAddress: function () {
            data.showEditEmailAddress.value = false;
        },
        cancelEditMobileNumber: function () {
            data.showEditMobileNumber.value = false;
        },
        cancelEditSecuritySettings: function () {
            data.showEditSecuritySettings.value = false;
        },
        cancelChangePassword: function () {
            data.showChangePassword.value = false;
        },
        cancelMoveUserAdmin: function () {
            data.showMoveUserAdmin.value = false;
        },
        uploadImage: async function (ev: any) {
            try {
                data.isLoading.value = true;

                let file = ev.target.files[0] as File;
                var manager = context.apiManager;
                const formData = new FormData();
                formData.set("file", file);

                let request = manager.createRequest({
                    service: 'accounts',
                    path: "/accounts/user/upload-profile-picture",
                    method: HttpMethod.POST,
                }, formData);

                delete request.headers['Content-Type'];

                var response = await fetch(request.url, {
                    body: formData,
                    headers: request.headers,
                    method: "POST"
                }).then(res => {
                    if (!res.ok) {
                        return { ok: false } as ApiResultModel<UserModel>
                    }

                    return res.json() as Promise<ApiResultModel<UserModel>>
                });

                if (response && response.ok && response.data) {
                    data.isLoading.value = false;
                    refreshUser();
                }
            } catch (err) {
                console.error(err);
            } finally {
                data.isLoading.value = false;
            }
        },
        loginAs: async function () {

            if (!data.isLoading.value) {
                try {
                    data.isLoading.value = true;

                    const manager = context.apiManager;
                    let model: LoginWithUsernameModel = {
                        username: data.userProfile.value.email
                    };

                    let req = manager.createRequest({
                        service: 'main',
                        path: '/accounts/log-on-as',
                        method: HttpMethod.POST,
                    }, model);

                    var res = await manager.get<ApiResultModel>(req);

                    if (res.ok) {
                        window.location.href = "/";
                    }

                } catch (err) {
                    console.error(err);
                } finally {
                    data.isLoading.value = false;
                }
            }
        },
        changeStatusSaveCheck: async function (confirmed: boolean) {
            if (data.changeStatusModel.value.status == UserStatus.Purge && !confirmed) {
                data.showDeleteUserModal.value = true;
            } else {
                await changeStatusSave(data);
            }
        },
        addClaim: async function (key: string, value: string) {
            if (!data.isLoading.value) {
                data.isLoading.value = true;

                try {
                    let userManager = await context.getService<IUserManager>('IUserManager');
                    var response = await userManager.addClaim(
                        {
                            id: data.userProfile.value.id,
                            type: key,
                            value: value
                        } as IdClaimModel);

                    if (!response || !response.ok) {
                        console.log("Error adding user claim: " + response.message);
                        showMessage("Error adding user claim.", "danger", true, true);
                    } else {
                        showMessage("User claim was successfully added.", "success", true, true);
                        data.isLoading.value = false;
                        closeModals(data);

                        if (data.userProfile.value.id == context.user?.id) {
                            refreshUser();
                        } else {
                            await reloadUserModel(data);
                        }
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    data.isLoading.value = false;
                }
            }
        },
        removeClaim: async function (key: string, value: string) {
            if (!data.isLoading.value) {
                data.isLoading.value = true;

                try {
                    let userManager = await context.getService<IUserManager>('IUserManager');
                    var response = await userManager.removeClaim({
                        id: data.userProfile.value.id,
                        type: key,
                        value: value
                    } as IdClaimModel);

                    if (!response || !response.ok) {
                        console.log("Error removing user claim: " + response.message);
                        showMessage("Error removing user claim.", "danger", true, true);
                    } else {
                        showMessage("User claim was successfully removed.", "success", true, true);
                        data.isLoading.value = false;
                        closeModals(data);
                        if (data.userProfile.value.id == context.user?.id) {
                            refreshUser();
                        } else {
                            await reloadUserModel(data);
                        }
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    data.isLoading.value = false;
                }
            }
        },
        removeProfilePicture: async function () {
            if (!data.isLoading.value) {
                data.isLoading.value = true;

                try {
                    let userManager = await context.getService<IUserManager>('IUserManager');
                    var response = await userManager.removeProfilePicture(data.userProfile.value.id);

                    if (!response || !response.ok) {
                        console.log("Error removing profile picture: " + response.message);
                        showMessage("Error removing profile picture.", "danger", true, true);
                    } else {
                        showMessage("User Profile Picture successfully removed.", "success", true, true);
                        data.isLoading.value = false;
                        closeModals(data);
                        refreshUser();
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    data.isLoading.value = false;
                }
            }
        },
        updateUser: function (userProfile: UserModel) {
            showMessage("User updated successfully", "success", true, true);
            data.userProfile.value = userProfile;
            closeModals(data);

            if (userProfile.id == context.user?.id) {
                refreshUser();
            }
        }
    }
}

export default defineComponent({
    name: "UserProfile",
    props: {
        userProfile: {
            type: Object as () => UserModel,
            required: true
        },
        timeZones: {
            type: Array as () => string[],
            required: false
        }
    },
    components: {
        'edit-admin-settings': editAdminSettings,
        'edit-user-info': editUserInfo,
        'edit-user-preferences': editUserPreferences,
        'edit-email-address': editEmailAddress,
        'edit-mobile-number': editMobileNumber,
        'edit-security-settings': editSecuritySettings,
        'change-password': changePassword,
        'move-user-admin': moveUserAdmin
    },
    setup: function (props, ctx: any) {
        let data = getInitialData(props);
        let methods = getMethods(data, props, ctx);

        onMounted(async () => {
            var lookupService = await context.getService<ILookupService>('ILookupService');
            data.allServices.value = await lookupService.getServices({
                sites: [context.system],
                status: ServiceStatus.Active,
                types: [ServiceType.Service, ServiceType.DealerNetwork]
            } as ServiceLookupModel)
        });

        return {
            ...data,
            ...methods,
            getUserRoles,
            isUserInRole,
            UserStatus
        }
    }
})